import React from 'react';
import { Box, Divider, Stack, Tooltip, Typography } from '@mui/material';

/**
 * The one shared "what does Accuracy mean?" hover bubble.
 *
 * Every surface that displays the WAPE-derived `accuracy_score` (trainings
 * table, pipeline header, training modal, tournament board, forecast studio,
 * goal cards, inference detail) wraps its number with this component, so the
 * explanation is word-for-word identical everywhere. Single source of truth
 * for the copy — do not fork this text into per-surface tooltips.
 *
 * Props:
 *   value     accuracy on the 0–100 scale, or null/undefined when not measured
 *   scoreKind 'classification' switches to the AUC (class separation) copy
 *   backtest  optional { folds, windows, horizon, points } context row
 *   note      optional extra line (e.g. "Models are ranked by CRPS")
 *   placement MUI tooltip placement (default 'top')
 *   children  the anchor element (must accept a ref — wrap plain function
 *             components in a <Box> at the call site)
 */

const fmt1 = (v) => `${Number(v).toFixed(1)}%`;

const BubbleContent = ({ value, scoreKind, backtest, note }) => {
  const isClf = scoreKind === 'classification';
  const hasValue = typeof value === 'number' && !Number.isNaN(value);
  const wape = hasValue ? Math.max(0, 100 - Number(value)) : null;

  const backtestParts = [];
  if (backtest?.folds > 0) {
    backtestParts.push(`${backtest.folds} walk-forward fold${backtest.folds === 1 ? '' : 's'}`);
  }
  if (backtest?.windows > 0) {
    backtestParts.push(`${backtest.windows} validation window${backtest.windows === 1 ? '' : 's'}`);
  }
  if (backtest?.horizon > 0) {
    backtestParts.push(`${backtest.horizon}-step horizon`);
  }
  if (backtest?.points > 0) {
    backtestParts.push(`${backtest.points} held-out points`);
  }

  return (
    <Stack spacing={0.75} sx={{ p: 1.5, maxWidth: 320 }}>
      <Stack direction="row" spacing={1} alignItems="baseline">
        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {isClf ? 'Class separation' : 'Accuracy'}
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, lineHeight: 1.2, color: hasValue ? 'primary.main' : 'text.disabled' }}
        >
          {hasValue ? fmt1(value) : '—'}
        </Typography>
      </Stack>

      {isClf ? (
        <Typography variant="caption" sx={{ display: 'block' }}>
          How cleanly the model separates the two classes on held-out data
          (ROC AUC). 50% = a coin flip, 100% = perfect separation.
        </Typography>
      ) : (
        <>
          <Typography variant="caption" sx={{ display: 'block' }}>
            How close this model’s forecasts were to what actually happened.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Measured on a backtest: the model forecast past periods it never
            saw during training, and those forecasts were compared with the
            real values.
          </Typography>
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Accuracy = 100% − WAPE, where WAPE is the total miss as a share of
            the total actuals.
            {hasValue && (
              <>
                {' '}Here the forecasts were off by{' '}
                <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {fmt1(wape)}
                </Box>{' '}
                of the total.
              </>
            )}
          </Typography>
        </>
      )}

      {!hasValue && (
        <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
          Not evaluated yet for this item.
        </Typography>
      )}

      {backtestParts.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Backtest: {backtestParts.join(' · ')}
        </Typography>
      )}

      {note && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {note}
        </Typography>
      )}

      {!isClf && (
        <>
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
            High accuracy on calm data is easy — check Skill to see whether the
            model beats the naive baseline (yesterday’s value carried forward).
          </Typography>
        </>
      )}
    </Stack>
  );
};

const AccuracyInfoTooltip = ({
  value,
  scoreKind,
  backtest,
  note,
  placement = 'top',
  children,
}) => (
  <Tooltip
    arrow
    placement={placement}
    enterDelay={150}
    title={<BubbleContent value={value} scoreKind={scoreKind} backtest={backtest} note={note} />}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: 'background.paper',
          color: 'text.primary',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          boxShadow: 6,
          p: 0,
          maxWidth: 'none',
        },
      },
      arrow: {
        sx: {
          color: 'background.paper',
          '&::before': { border: '1px solid', borderColor: 'divider' },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

export default AccuracyInfoTooltip;
